import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useFloatingPosition from '../hooks/useFloatingPosition';

function SectionSelect({ sections, value, onChange, labelId }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const style = useFloatingPosition({
    open,
    anchorRef: buttonRef,
    floatRef: listRef,
    matchWidth: true,
    onDismiss: close
  });

  const selectedIndex = Math.max(0, sections.findIndex((s) => s.id === value));
  const count = sections.length;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (!buttonRef.current?.contains(event.target) && !listRef.current?.contains(event.target)) {
        close();
      }
    };
    document.addEventListener('mousedown', onDown);
    listRef.current?.focus({ preventScroll: true });
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const choose = (index) => {
    onChange(sections[index].id);
    close();
    buttonRef.current?.focus();
  };

  const onButtonKey = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(selectedIndex);
      setOpen(true);
    }
  };

  const onListKey = (event) => {
    const keys = {
      ArrowDown: () => setActive((i) => Math.min(i + 1, count - 1)),
      ArrowUp: () => setActive((i) => Math.max(i - 1, 0)),
      Home: () => setActive(0),
      End: () => setActive(count - 1),
      Enter: () => choose(active),
      ' ': () => choose(active)
    };
    if (keys[event.key]) {
      event.preventDefault();
      keys[event.key]();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      buttonRef.current?.focus();
    } else if (event.key === 'Tab') {
      close();
    }
  };

  return (
    <>
      <button
        type="button"
        ref={buttonRef}
        className="select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        onKeyDown={onButtonKey}
        onClick={() => {
          setActive(selectedIndex);
          setOpen((v) => !v);
        }}
      >
        <span className="select-value">{sections[selectedIndex]?.title}</span>
        <span className="select-caret" aria-hidden="true">▾</span>
      </button>
      {open &&
        createPortal(
          <div
            className="select-list"
            role="listbox"
            tabIndex={-1}
            ref={listRef}
            style={style}
            aria-labelledby={labelId}
            aria-activedescendant={`${labelId}-opt-${active}`}
            onKeyDown={onListKey}
          >
            {sections.map((section, index) => (
              <div
                key={section.id}
                id={`${labelId}-opt-${index}`}
                role="option"
                aria-selected={section.id === value}
                className={`select-option${index === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(index)}
              >
                <span className="select-value">{section.title}</span>
                {section.id === value && <span aria-hidden="true">✓</span>}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

export default SectionSelect;